import { FC, useCallback } from 'react';
import { useNavigate } from 'react-router';
import { useAppDispatch } from 'hooks/redux';
import { useIntl } from 'react-intl';
import { format } from 'date-fns';
import { Device } from 'models/connectivity';
import * as routes from 'router/Routes';
import { SortOrder } from 'utils/array';
import IconCircle from 'components/UI/IconCircle';
import StarButton from 'components/UI/StarButton';
import SortableTable, { HeaderCell } from 'components/UI/Table/SortableTable';
import { storeFavoriteDevice } from 'store/connectivityState/connectivityState';

const headerCells: HeaderCell[] = [
  {
    title: 'connectivity.iprId',
    sortable: true,
  },
  {
    title: 'connectivity.articleNo',
    sortable: true,
  },
  {
    title: 'connectivity.articleDesc',
    sortable: true,
  },
  {
    title: 'connectivity.lastConnection',
    sortable: true,
  },
  {
    title: 'connectivity.status',
    sortable: false,
  },
  {
    title: 'connectivity.softwareUpdate',
    sortable: false,
  },
  {
    title: 'connectivity.favoriteDevice',
    sortable: false,
  },
];

type Props = {
  devices: Device[];
};

const DeviceTable: FC<Props> = ({ devices }) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const intl = useIntl();

  // Fetch devices and navigate
  const onClick = useCallback(
    (iprId: string) => {
      navigate(`${routes.DEVICES}/${iprId}`);
    },
    [navigate]
  );

  const updateFavorite = useCallback(
    (iprId: string, isFavoriteDevice: boolean) => {
      dispatch(storeFavoriteDevice(iprId, isFavoriteDevice));
    },
    [dispatch]
  );

  // Table rows
  const bodyRows = devices.map(({ id, attributes }) => {
    const {
      iprId,
      pnc,
      status,
      articleDesc,
      softwareUpdate,
      lastConnection,
      isFavoriteDevice,
    } = attributes;

    return {
      id,
      pnc: pnc || '–',
      description: articleDesc || '–',
      lastConnection:
        lastConnection && lastConnection > 0
          ? format(Number(lastConnection), 'yyyy-MM-dd HH:mm:ss')
          : '-',
      status: status ? (
        <IconCircle size="16px" text={status} status={status} />
      ) : (
        '–'
      ),
      softwareUpdate: softwareUpdate ? (
        <IconCircle size="16px" text={softwareUpdate} status={softwareUpdate} />
      ) : (
        '–'
      ),
      favorite: (
        <StarButton
          text={
            isFavoriteDevice
              ? intl.formatMessage({ id: 'connectivity.remove_favorite' })
              : intl.formatMessage({ id: 'connectivity.add_favorite' })
          }
          active={isFavoriteDevice}
          onClick={() => updateFavorite(iprId, !isFavoriteDevice)}
        />
      ),
      link: iprId,
    };
  });

  return (
    <SortableTable
      headerCells={headerCells}
      bodyRows={bodyRows}
      onClick={onClick}
      sortOrder={SortOrder.Desc}
      sortIndex={3}
    />
  );
};

export default DeviceTable;
